'use client';

import { useEffect, useCallback } from 'react';
import { useLayoutStore } from '../../store/layoutStore';
import { useWebSocketStore } from '../../store/websocket-store';
import { z } from 'zod';

const OpenStreamingSessionPayloadSchema = z.object({
  sessionId: z.string(),
  type: z.enum(['sse', 'websocket']),
  url: z.string(),
  isActive: z.boolean().optional(),
  source: z.enum(['copilot', 'neo']).optional(),
});

export function StreamingSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const setSidebarTab = useLayoutStore((state) => state.setSidebarTab);
  const openMainContentTab = useLayoutStore(
    (state) => state.openMainContentTab,
  );

  const handleOpenStreamingSession = useCallback(
    (event: CustomEvent) => {
      const parsed = OpenStreamingSessionPayloadSchema.safeParse(
        event.detail || {},
      );
      if (!parsed.success) return;

      const {
        sessionId,
        url,
        source = 'copilot',
        isActive = true,
      } = parsed.data;

      const { upsertAgentConnection, selectConnection, connections } =
        useWebSocketStore.getState();

      const connectionId = upsertAgentConnection({
        streamingSessionId: sessionId,
        url,
        source,
        status: isActive ? 'open' : 'closed',
      });

      // Select the connection so it is highlighted in the grid
      selectConnection(connectionId);

      // If the connection already has messages queued in the ws store, they're already there.
      // Sync any remaining messages from the store for this session
      const existingConn = connections[connectionId];
      if (!existingConn) {
        // newly created — nothing more to do
      }

      setSidebarTab('websocket');
      openMainContentTab({
        id: 'websocket',
        title: 'WS / SSE',
        type: 'websocket',
      });
    },
    [setSidebarTab, openMainContentTab],
  );

  useEffect(() => {
    window.addEventListener(
      'terra-open-streaming-session',
      handleOpenStreamingSession as unknown as EventListener,
    );
    return () => {
      window.removeEventListener(
        'terra-open-streaming-session',
        handleOpenStreamingSession as unknown as EventListener,
      );
    };
  }, [handleOpenStreamingSession]);

  return children;
}
